import { useEffect, useState } from 'react';
import { catalogApi, type TenantCourt, type NewTenantCourt } from '../../api';
import Select from '../../components/common/Select';

const TIERS = [
  { value: 'constitutional',   label: 'Constitutional Court' },
  { value: 'sca',              label: 'Supreme Court of Appeal' },
  { value: 'high',             label: 'High Court' },
  { value: 'specialised',      label: 'Specialised court (Labour, Land, Tax, Competition, etc.)' },
  { value: 'magistrate_court', label: 'Magistrate\u2019s Court (District / Regional in same seat)' },
  { value: 'small_claims',     label: 'Small Claims Court' },
  { value: 'tribunal',         label: 'Tribunal' },
  { value: 'arbitration',      label: 'Arbitration panel' },
  { value: 'other',            label: 'Other forum' },
];

const BLANK: NewTenantCourt = {
  code: '', name: '', tier: 'tribunal',
  division: '', seat: '', notes: '',
  court_online_enabled: false, caselines_enabled: false, display_order: 0,
};

export default function SettingsCourts() {
  const [rows, setRows] = useState<TenantCourt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<NewTenantCourt>(BLANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await catalogApi.listTenantCourts();
      setRows(r.courts);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (row: TenantCourt) => {
    setEditingId(row.ROWID);
    setForm({
      code: row.code, name: row.name, tier: row.tier,
      division: row.division || '', seat: row.seat || '', notes: row.notes || '',
      court_online_enabled: row.court_online_enabled === true || row.court_online_enabled === 'true',
      caselines_enabled:    row.caselines_enabled    === true || row.caselines_enabled    === 'true',
      display_order: row.display_order ?? 0,
    });
  };

  const cancel = () => { setEditingId(null); setForm(BLANK); };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      if (editingId) await catalogApi.updateTenantCourt(editingId, form);
      else           await catalogApi.createTenantCourt(form);
      cancel();
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  const remove = async (row: TenantCourt) => {
    if (!confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    try {
      await catalogApi.deleteTenantCourt(row.ROWID);
      await load();
    } catch (e) { alert((e as Error).message); }
  };

  const isTrue = (v: unknown) => v === true || v === 'true';

  return (
    <div className="stack" style={{ maxWidth: 880 }}>
      <div>
        <h1>Courts &amp; forums</h1>
        <p className="muted">
          The SA courts catalog (Constitutional Court through Magistrates) is system-managed and always available.
          Add your own tribunals, arbitration panels or specialised fora here — they're visible only to this chambers.
        </p>
      </div>

      <div className="card stack">
        <h3 style={{ margin: 0 }}>{editingId ? 'Edit forum' : 'Add a custom forum'}</h3>

        <div className="row">
          <div style={{ width: 180 }}>
            <label>Short code</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. CCMA-JHB" />
          </div>
          <div className="grow">
            <label>Display name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. CCMA Johannesburg" />
          </div>
        </div>

        <div className="row">
          <div className="grow">
            <label>Tier</label>
            <Select
              value={form.tier}
              onChange={(v) => setForm({ ...form, tier: v })}
              options={TIERS}
              searchPlaceholder="Search tiers…"
            />
          </div>
          <div className="grow">
            <label>Division (optional)</label>
            <input value={form.division || ''} onChange={(e) => setForm({ ...form, division: e.target.value })} placeholder="e.g. Gauteng Local" />
          </div>
          <div className="grow">
            <label>Seat (optional)</label>
            <input value={form.seat || ''} onChange={(e) => setForm({ ...form, seat: e.target.value })} placeholder="e.g. Johannesburg" />
          </div>
        </div>

        <div>
          <label>Notes (optional)</label>
          <textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="row" style={{ gap: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={!!form.court_online_enabled} onChange={(e) => setForm({ ...form, court_online_enabled: e.target.checked })} style={{ width: 'auto' }} />
            <span>CourtOnline enabled</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={!!form.caselines_enabled} onChange={(e) => setForm({ ...form, caselines_enabled: e.target.checked })} style={{ width: 'auto' }} />
            <span>CaseLines enabled</span>
          </label>
        </div>

        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {editingId && <button onClick={cancel}>Cancel</button>}
          <button className="primary" onClick={save} disabled={saving || !form.code || !form.name}>
            {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add forum')}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }} className="grow">Your custom forums ({rows.length})</h3>
          <button onClick={load}>Refresh</button>
        </div>
        {loading ? <div className="muted">Loading…</div> : rows.length === 0 ? (
          <p className="muted">You haven't added any custom forums yet. System SA courts are already available on every matter.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Code</th><th>Name</th><th>Tier</th><th>Seat</th><th>Active</th><th style={{ width: 160 }}></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ROWID}>
                  <td><code style={{ fontSize: 12 }}>{r.code}</code></td>
                  <td>{r.name}</td>
                  <td><span className="pill">{TIERS.find((t) => t.value === r.tier)?.label || r.tier}</span></td>
                  <td>{r.seat || '—'}</td>
                  <td>{isTrue(r.active) ? 'Yes' : <span className="muted">No</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => startEdit(r)}>Edit</button>
                    <button onClick={() => remove(r)} style={{ marginLeft: 6 }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
